import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError, api } from "@/api/client";
import type { IncidentDetail, PendingApproval } from "@/api/types";
import { ApprovalCard } from "@/components/ApprovalCard";
import { ChatInput, ChatInputSubmit } from "@/components/ChatInput";
import { ChatMessage } from "@/components/ChatMessage";
import { IncidentContext } from "@/components/IncidentContext";
import { LiveAnalysisActivity } from "@/components/LiveAnalysisActivity";
import { Button } from "@/components/ui/button";
import {
  useAnalysisSession,
  useCloseSession,
} from "@/hooks/useAnalysis";
import {
  startAnalysisDeferred,
  useAnalysisStream,
} from "@/hooks/useAnalysisStream";
import { usePendingApprovals } from "@/hooks/useApprovals";
import { useCommandDispatcher, CommandResponse } from "@/hooks/useCommandDispatcher";

export function AnalysisPage() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const startedForIncident = useRef<string | null>(null);

  // F14: arrancamos la sesión con defer_first_turn=true. La navegación
  // al chat es instantánea (no esperamos al primer turno), y el motor
  // razona en vivo a través del stream que abre useAnalysisStream.
  useEffect(() => {
    if (!incidentId) return;
    if (startedForIncident.current === incidentId) return;
    startedForIncident.current = incidentId;
    startAnalysisDeferred({ trigger_type: "manual", incident_id: incidentId })
      .then((data) => {
        setSessionId(data.id);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 503) {
          setBootError(
            "El módulo de análisis profundo no está configurado en este servidor.",
          );
        } else if (err instanceof ApiError && err.status === 429) {
          setBootError("Cupo mensual de tokens agotado.");
        } else {
          setBootError(
            err instanceof Error ? err.message : "Error iniciando sesión.",
          );
        }
      });
  }, [incidentId]);

  const sessionQuery = useAnalysisSession(sessionId ?? undefined);
  const closeMutation = useCloseSession(sessionId ?? undefined);
  const pendingApprovalsQuery = usePendingApprovals();
  const incidentQuery = useIncidentDetail(incidentId);
  const [commandResponses, setCommandResponses] = useState<CommandResponse[]>([]);
  const dispatch = useCommandDispatcher({ incidentId: incidentId ?? null });

  const stream = useAnalysisStream(sessionId ?? undefined);
  const kickoff = stream.kickoff;

  // Disparar el primer turno apenas conocemos sessionId y el snapshot
  // inicial (con el user message sintético) llegó. Solo una vez por
  // sesión: el flag vive en un ref para no depender del re-render.
  const kickoffSentFor = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    if (kickoffSentFor.current === sessionId) return;
    const data = sessionQuery.data;
    if (!data) return;
    // Solo arrancar si la sesión está activa y aún no tiene respuesta del
    // motor (defer_first_turn). Si por alguna razón ya tiene assistant,
    // skip — significa que la sesión fue resumida.
    const hasAssistant = data.messages.some((m) => m.role === "assistant");
    if (hasAssistant) {
      kickoffSentFor.current = sessionId;
      return;
    }
    if (data.status !== "active") return;
    kickoffSentFor.current = sessionId;
    kickoff();
  }, [sessionId, sessionQuery.data, kickoff]);

  const sessionApprovals = useMemo(() => {
    const all = pendingApprovalsQuery.data ?? [];
    return sessionId
      ? all.filter((a) => a.analysis_session_id === sessionId)
      : [];
  }, [pendingApprovalsQuery.data, sessionId]);

  const handleSubmit = async (payload: ChatInputSubmit) => {
    if (!sessionId) return;
    if (payload.type === "message" && payload.content) {
      stream.sendMessage(payload.content);
      return;
    }
    if (payload.type === "command" && payload.command) {
      const response = await dispatch(payload.command, payload.args ?? []);
      setCommandResponses((prev) => [...prev, response]);
    }
  };

  const handleClose = () => {
    if (!sessionId) return;
    closeMutation.mutate(undefined, {
      onSuccess: () => navigate(`/incidents/${incidentId}`),
    });
  };

  if (bootError) {
    return (
      <div className="mx-auto max-w-2xl rounded border border-destructive/40 bg-destructive/10 p-4">
        <div className="font-semibold text-destructive">
          No se pudo iniciar la sesión
        </div>
        <div className="mt-1 text-sm">{bootError}</div>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate(`/incidents/${incidentId}`)}
        >
          ← Volver al incidente
        </Button>
      </div>
    );
  }

  if (!sessionId || sessionQuery.isLoading) {
    return (
      <div className="text-sm text-muted-foreground">Iniciando análisis…</div>
    );
  }

  const session = sessionQuery.data;
  if (!session) {
    return (
      <div className="text-sm text-muted-foreground">Cargando sesión…</div>
    );
  }

  const isClosed =
    session.status === "completed" || session.status === "cancelled";
  const isStreaming = stream.status === "streaming";

  return (
    <div className="grid h-[calc(100vh-4rem)] grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,30%)_1fr]">
      <div className="space-y-4 overflow-auto">
        {incidentQuery.data && <IncidentContext incident={incidentQuery.data} />}
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
            Sesión
          </div>
          <div className="space-y-1 text-sm">
            <div>
              Estado: <span className="font-mono">{session.status}</span>
            </div>
            <div>
              Motor: <span className="font-mono">{session.model_id}</span>
            </div>
            <div>
              Tokens: <span className="font-mono">{session.token_usage.total_tokens}</span>
            </div>
            {isStreaming && (
              <div className="text-xs italic text-muted-foreground">
                AROC razonando en vivo…
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={handleClose}
            disabled={isClosed || closeMutation.isPending}
          >
            Cerrar sesión
          </Button>
          {isStreaming && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full"
              onClick={stream.cancel}
            >
              Cancelar turno
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col rounded-lg border bg-card overflow-hidden">
        <div className="flex-1 space-y-3 overflow-auto p-4">
          {session.messages.map((m, i) => (
            <ChatMessage key={`${m.timestamp}-${i}`} message={m} />
          ))}
          <LiveAnalysisActivity
            liveTokens={stream.liveTokens}
            liveToolCalls={stream.liveToolCalls}
            status={stream.status}
            errorMessage={stream.errorMessage}
          />
          {sessionApprovals.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                Acciones propuestas
              </div>
              {sessionApprovals.map((a: PendingApproval) => (
                <ApprovalCard key={a.id} approval={a} />
              ))}
            </div>
          )}
          {commandResponses.map((r, i) => (
            <div
              key={`${r.timestamp}-${i}`}
              className={
                r.ok
                  ? "rounded border bg-muted/40 px-3 py-2 font-mono text-xs"
                  : "rounded border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-xs text-destructive"
              }
            >
              <pre className="whitespace-pre-wrap">{r.text}</pre>
            </div>
          ))}
        </div>
        <ChatInput
          onSubmit={handleSubmit}
          disabled={isStreaming || isClosed}
          placeholder={
            isClosed
              ? "Sesión cerrada"
              : "Escribe un mensaje o /comando…"
          }
        />
      </div>
    </div>
  );
}

function useIncidentDetail(incidentId: string | undefined) {
  return useQuery({
    queryKey: ["incident-detail", incidentId],
    queryFn: () => api<IncidentDetail>(`/api/v1/incidents/${incidentId}`),
    enabled: Boolean(incidentId),
  });
}
